import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles } from './signup.styles.js';
import { UserRole } from '@cmms/models';


@customElement('cmms-signup')
export class CMMSSignup extends LitElement {
  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private successMessage = '';

  // Form data
  @state()
  private formData = {
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    companyName: '',
    password: ''
  };

  static styles = styles;

  private handleFieldChange(field: string, value: any) {
    this.formData = { ...this.formData, [field]: value };
  }

  private validateForm(): string[] {
    const errors: string[] = [];

    // Validate required fields
    if (!this.formData.firstName.trim()) errors.push('First name is required');
    if (!this.formData.lastName.trim()) errors.push('Last name is required');
    if (!this.formData.email.trim()) errors.push('Email address is required');
    if (!this.formData.telephone.trim()) errors.push('Telephone number is required');
    if (!this.formData.companyName.trim()) errors.push('Company name is required');
    if (!this.formData.password.trim()) errors.push('Password is required');

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (this.formData.email && !emailRegex.test(this.formData.email)) {
      errors.push('Email must be a valid email address');
    }

    // Validate password strength
    if (this.formData.password && this.formData.password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    return errors;
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    
    this.errorMessage = '';
    this.successMessage = '';

    const validationErrors = this.validateForm();
    if (validationErrors.length > 0) {
      this.errorMessage = validationErrors.join('. ') + '.';
      return;
    }

    try {
      this.isLoading = true;

      // Prepare company data (only required fields for registration)
      const companyData = {
        name: this.formData.companyName,
        phone_number: this.formData.telephone,
        email_address: this.formData.email
      };

      // Prepare user data
      const userData = {
        name: `${this.formData.firstName} ${this.formData.lastName}`,
        email: this.formData.email,
        password: this.formData.password
      };

      // Dispatch signup event with the data
      this.dispatchEvent(new CustomEvent('signup-request', {
        detail: { 
          company: companyData, 
          user: userData 
        },
        bubbles: true,
        composed: true
      }));

      this.successMessage = 'Sign-up request submitted successfully! You will be contacted soon to complete your account setup.';
      
    } catch (error: any) {
      this.errorMessage = error.message || 'Sign-up failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private handleBackClick() {
    window.location.href = '/';
  }

  render() {
    return html`
      <div class="signup-container">
        <div class="signup-card">
          <div class="signup-header">
            <sl-icon name="building" style="font-size: 2rem;"></sl-icon>
            <h2>Create Your Account</h2>
            <p>Sign up for PristineMaint CMMS</p>
          </div>

          ${this.errorMessage ? html`
            <div class="error-message">
              <sl-icon name="exclamation-triangle"></sl-icon>
              ${this.errorMessage}
            </div>
          ` : ''}

          ${this.successMessage ? html`
            <div class="success-message">
              <sl-icon name="check-circle"></sl-icon>
              ${this.successMessage}
            </div>
          ` : ''}

          <form @submit=${this.handleSubmit} class="signup-form">
            <div class="form-section">
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName">First Name <span class="required-indicator">*</span></label>
                  <sl-input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    .value=${this.formData.firstName}
                    @sl-input=${(e: any) => this.handleFieldChange('firstName', e.target.value)}
                    required
                    size="medium"
                  >
                    <sl-icon name="person" slot="prefix"></sl-icon>
                  </sl-input>
                </div>

                <div class="form-group">
                  <label for="lastName">Last Name <span class="required-indicator">*</span></label>
                  <sl-input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    .value=${this.formData.lastName}
                    @sl-input=${(e: any) => this.handleFieldChange('lastName', e.target.value)}
                    required
                    size="medium"
                  >
                    <sl-icon name="person" slot="prefix"></sl-icon>
                  </sl-input>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="email">Email Address <span class="required-indicator">*</span></label>
                  <sl-input
                    id="email"
                    type="email"
                    placeholder="your.email@company.com"
                    .value=${this.formData.email}
                    @sl-input=${(e: any) => this.handleFieldChange('email', e.target.value)}
                    required
                    size="medium"
                  >
                    <sl-icon name="envelope" slot="prefix"></sl-icon>
                  </sl-input>
                </div>

                <div class="form-group">
                  <label for="telephone">Telephone Number <span class="required-indicator">*</span></label>
                  <sl-input
                    id="telephone"
                    type="tel"
                    placeholder="+31 6 12345678"
                    .value=${this.formData.telephone}
                    @sl-input=${(e: any) => this.handleFieldChange('telephone', e.target.value)}
                    required
                    size="medium"
                  >
                    <sl-icon name="telephone" slot="prefix"></sl-icon>
                  </sl-input>
                </div>
              </div>

              <div class="form-row single">
                <div class="form-group">
                  <label for="companyName">Company Name <span class="required-indicator">*</span></label>
                  <sl-input
                    id="companyName"
                    type="text"
                    placeholder="Enter company name"
                    .value=${this.formData.companyName}
                    @sl-input=${(e: any) => this.handleFieldChange('companyName', e.target.value)}
                    required
                    size="medium"
                  >
                    <sl-icon name="building" slot="prefix"></sl-icon>
                  </sl-input>
                </div>
              </div>

              <div class="form-row single">
                <div class="form-group">
                  <label for="password">Password <span class="required-indicator">*</span></label>
                  <sl-input
                    id="password"
                    type="password"
                    placeholder="Enter password (min 8 characters)"
                    .value=${this.formData.password}
                    @sl-input=${(e: any) => this.handleFieldChange('password', e.target.value)}
                    required
                    size="medium"
                    password-toggle
                  >
                    <sl-icon name="lock" slot="prefix"></sl-icon>
                  </sl-input>
                </div>
              </div>
            </div>

            <div class="form-actions">
              <sl-button
                type="button"
                variant="default"
                size="medium"
                class="back-button"
                @click=${this.handleBackClick}
                ?disabled=${this.isLoading}
              >
                Back to Login
              </sl-button>
              
              <sl-button
                type="submit"
                variant="primary"
                size="medium"
                class="signup-button"
                ?loading=${this.isLoading}
                ?disabled=${this.isLoading}
              >
                ${this.isLoading ? 'Creating Account...' : 'Create Account'}
              </sl-button>
            </div>
          </form>

          <div class="signup-footer">
            <p>Already have an account? <a href="/" style="color: var(--sl-color-primary-600); text-decoration: none;">Sign in here</a></p>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cmms-signup': CMMSSignup;
  }
}
